import { MathsKitTool } from '../types';
import Matrix2D, { getAngleByMatrix, decomposeMatrix, createTranslationMatrix, composeTransforms, createRotationMatrix } from './matrix';
import { Point2d } from './Point2d';

export type operationType = 'Drag' | 'Rotate' | 'Stretch';

export interface BindOperationEventOptions {
  bindEvents: operationType[];
  getGlobalScale:()=>number;
  getInstance:(id:string)=>MathsKitTool | null;
  activeOperation:(bol:boolean)=>void;
  getContainerRect:()=>DOMRect;
}

export class OperationManager {
  private element: HTMLDivElement;
  private options: BindOperationEventOptions;
  private originalMatrix: Matrix2D | null = null;
  private originalClient: [number, number] = [0, 0];
  private newMatrix: Matrix2D | null = null;
  private originalWidth: number = 0;
  private newWidth: number = 0;
  private isTouchDevice: boolean = false;
  private activeInstance: MathsKitTool | null = null;
  private activeOperationType: operationType | null = null;
  private originalCursor: string | null = null;
  constructor(element:HTMLDivElement, options:BindOperationEventOptions) {
    this.element = element;
    this.options = options;
    this.element.addEventListener('pointerdown', this.onPointerdown, true);
  }

  private onPointerdown = (e:PointerEvent) => {
    const currentTarget = e.target as HTMLElement;
    if (!currentTarget) {
      return;
    }
    const currentTargetType = currentTarget?.getAttribute('data-type') || null;
    if (!currentTargetType) {
      return;
    }    
    for(const item of this.options.bindEvents){
      if(item === currentTargetType){
        let id: string | null = null;
        if (item === 'Drag') {
          id = currentTarget.getAttribute('data-id') || null;
        } else {
          id = currentTarget.parentElement?.getAttribute('data-id') || null;
        }
        if (id) {
          this.activeInstance = this.options.getInstance(id);
          this.activeOperationType = item;
          this.options.activeOperation(true);
        }
        break;
      }
    }
    // 检测是否为触摸设备
    this.isTouchDevice = e.pointerType === 'touch';
    if (this.activeInstance) {
      e.stopPropagation();
      this.originalMatrix = this.activeInstance.getDomMatrix();
      this.originalClient = [e.clientX, e.clientY];
      document.body.addEventListener('pointermove', this.onPointermove);
      document.body.addEventListener('pointerup', this.onPointerup);
      document.body.addEventListener('pointercancel', this.onPointerup);
      
      // 尝试捕获指针，如果失败则记录错误但不中断操作
      // 对于触摸设备，setPointerCapture 可能会导致意外的 pointercancel 事件
      try {
        // 只在非触摸设备上使用 setPointerCapture，或者根据具体需求决定
        if (!this.isTouchDevice) {
          this.element.setPointerCapture(e.pointerId);
          console.log('Pointer capture set for non-touch device');
        } else {
          console.log('Skipping pointer capture for touch device');
        }
      } catch (error) {
        console.warn('Failed to set pointer capture:', error);
      }
      this.originalCursor = this.element.style.cursor;
      switch (this.activeOperationType) {
        case 'Drag':
          this.activeInstance.onDragStart();
          this.element.style.cursor = 'move';
          break;
        case 'Rotate': {
          const originAngle = getAngleByMatrix(this.originalMatrix);
          this.activeInstance.onRotateStart(originAngle);
          this.element.style.cursor = 'none';
          break;
        }
        case 'Stretch': {
          this.originalWidth = this.activeInstance.element.offsetWidth; // 初始化原始宽度
          this.newWidth = this.originalWidth;
          this.activeInstance.onStretchStart();
          this.element.style.cursor = 'ew-resize';
          break;
        }
        default:
          break;
      }
      this.activeInstance.element.style.pointerEvents = 'none';
    }
  };

  private onPointermove = (e:PointerEvent) => {
    e.stopPropagation();
    if (!this.activeOperationType || !this.activeInstance || !this.originalMatrix) return;
    switch (this.activeOperationType) {
      case 'Drag': {
        const containerRect = this.options.getContainerRect();
        const isOutSideX = e.clientX <= containerRect.left || e.clientX >= containerRect.right;
        const isOutSideY = e.clientY <= containerRect.top || e.clientY >= containerRect.bottom;
        if (isOutSideX || isOutSideY) {
          return;
        }
        const globalScale = this.options.getGlobalScale();
        // 计算鼠标移动的原始向量
        const rawDragX = Math.round((e.clientX - this.originalClient[0]) / globalScale);
        const rawDragY = Math.round((e.clientY - this.originalClient[1]) / globalScale);
        // 从原始矩阵中提取旋转角度
        const transform = decomposeMatrix(this.originalMatrix);
        const rotation = transform.rotation;
        
        // 根据旋转角度变换拖拽向量
        const cos = Math.cos(rotation); // 使用逆时针向旋转
        const sin = Math.sin(rotation);
        const transformedDragX = rawDragX * cos - rawDragY * sin;
        const transformedDragY = rawDragX * sin + rawDragY * cos;
        const translateMatrix = createTranslationMatrix(transformedDragX, transformedDragY);
        this.newMatrix = composeTransforms(this.originalMatrix, translateMatrix);
        this.activeInstance.element.style.transform = this.newMatrix.toCSSTransform();
        this.activeInstance.onDragDoing();
        break;
      }
      case 'Rotate': {
        const currentPoint = new Point2d(e.clientX, e.clientY);
        const originPoint = new Point2d(...this.originalClient);
        const containerRect = this.element.getBoundingClientRect();
        const centerPoint = this.activeInstance.getRotateCenterPoint().add(new Point2d(containerRect.left, containerRect.top));
        let angleInDegrees = centerPoint.getAngleByPoints(currentPoint, originPoint);
        angleInDegrees = Math.round(angleInDegrees * 100) / 100;
        const angleInRadians = (angleInDegrees * Math.PI) / 180; // 将角度转换为弧度
        // 基于原始矩阵创建新的旋转矩阵，而不是累积旋转
        const rotationMatrix = createRotationMatrix(angleInRadians);
        // 计算target元素的旋转中心点和元素中心的偏移
        this.newMatrix = composeTransforms(this.originalMatrix, rotationMatrix);
    
        this.activeInstance.element.style.transform = this.newMatrix.toCSSTransform();
    
        angleInDegrees = angleInDegrees + getAngleByMatrix(this.originalMatrix);
        // 确保角度为正数：逆时针为正，顺时针为360-角度
        if (angleInDegrees < 0) {
          angleInDegrees = 360 + angleInDegrees;
        } else if (angleInDegrees > 360) {
          angleInDegrees = angleInDegrees - 360;
        }
        this.activeInstance.onRotateDoing(angleInDegrees);
        break;
      }
      case 'Stretch': {
        const globalScale = this.options.getGlobalScale();
        // 获取当前旋转角度
        const transform = decomposeMatrix(this.originalMatrix);
        const rotation = transform.rotation;
        
        // 将鼠标移动向量按照旋转角度进行变换
        const cos = Math.cos(rotation);
        const sin = Math.sin(rotation);
        const currentPoint = new Point2d(e.clientX, e.clientY);
        const stretchDirection = this.activeInstance.getStretchDirection();
        let rawDragX = 0;
        let rawDragY = 0;
        let transformedDragX = 0;
        if (stretchDirection === 'x') {
          rawDragX = Math.round((currentPoint.x - this.originalClient[0]) / globalScale);
          rawDragY = Math.round((currentPoint.y - this.originalClient[1]) / globalScale);
          transformedDragX = rawDragX * cos + rawDragY * sin;
        } else {
          rawDragX = Math.round((currentPoint.x - this.originalClient[0]) / globalScale);
          rawDragY = Math.round((currentPoint.y - this.originalClient[1]) / globalScale);
          transformedDragX = -rawDragY * cos + rawDragX * sin;
        }
        
        // 计算新的宽度（只考虑 X 轴方向的拉伸）
        this.newWidth = Math.max(200, this.originalWidth + transformedDragX);
        
        // 更新元素的宽度
        this.activeInstance.element.style.width = `${this.newWidth}px`;
        
        // 触发变化回调
        this.activeInstance.onStretchDoing(this.newWidth);
        break;
      }
      default:
        break;
    }
  };

  private onPointerup = (e:PointerEvent) => {
    if(this.activeInstance && this.activeOperationType){
      e.stopPropagation();
      this.onPointermove(e);
      this.element.releasePointerCapture(e.pointerId);
      switch (this.activeOperationType) {
        case 'Drag':
          if (this.newMatrix) {
            this.activeInstance.onDragEnd(this.newMatrix);
          }
          break;
        case 'Rotate': {
          if (this.newMatrix) {
            this.activeInstance.onDragEnd(this.newMatrix);
          }
          break;
        }
        case 'Stretch':
          this.activeInstance.onStretchEnd(this.newWidth);
          break;
        default:
          break;
      }
      this.element.style.cursor = this.originalCursor || 'default';
      this.activeInstance.element.style.pointerEvents = 'auto';
      this.removeEventListener();
      this.options.activeOperation(false);
    }
  };

  private removeEventListener() {
    this.activeInstance = null;
    this.activeOperationType = null;
    this.originalMatrix = null;
    this.newMatrix = null;
    this.originalClient = [0, 0];
    this.originalWidth = 0;
    this.newWidth = 0;
    document.body.removeEventListener('pointermove', this.onPointermove);
    document.body.removeEventListener('pointerup', this.onPointerup);
  }

  private stop(){
    this.removeEventListener();
  }

  destroy(){
    this.stop();
    this.element.removeEventListener('pointerdown', this.onPointerdown, true);
  }

}